import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, BellRing, Radio, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import NewsletterForm from "@/components/NewsletterForm";

const NotifyButton = () => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["live-alerts-me"], queryFn: () => api.get("/media-hub/live-alerts").then((r) => r.data) });
  const on = !!data?.subscribed;
  const toggle = async () => {
    try {
      const { data: d } = await api.post("/media-hub/live-alerts", { on: !on });
      qc.setQueryData(["live-alerts-me"], d);
      toast.success(d.subscribed ? "We'll let you know the moment we go live" : "Live alerts turned off");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <button onClick={toggle} disabled={isLoading} data-testid="media-live-notify-btn" aria-pressed={on}
      className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${on ? "bg-[#5C8A72] text-white hover:bg-[#4d7561]" : "bg-[#144F75] text-white hover:bg-[#0F3A56]"}`}>
      {isLoading ? <Loader2 size={15} className="animate-spin" /> : on ? <BellRing size={15} /> : <Bell size={15} />}
      {on ? "You'll be notified — tap to turn off" : "Notify me when we go live"}
    </button>
  );
};

// Placeholder when nothing is live or scheduled; `compact` = a slim nudge under a schedule.
export const LiveAlertCTA = ({ compact = false }) => {
  const { user } = useAuth();
  if (compact) {
    return (
      <div className="mt-5 flex flex-wrap items-center gap-3 text-sm text-[#4A5D6B]" data-testid="media-live-notify-compact">
        {user ? <NotifyButton /> : <span>Want a heads-up when we go live? <Link to="/login?next=%2Fmedia%3Ftab%3Dlive" className="text-[#144F75] font-medium underline">Sign in</Link> to turn on live alerts.</span>}
      </div>
    );
  }
  return (
    <section className="relative overflow-hidden rounded-3xl bg-[#F5F1EA] border border-[#E5E0D8] p-8 md:p-12 mb-14" data-testid="media-live-placeholder">
      <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-[#D98E73]/10" aria-hidden />
      <div className="absolute right-20 bottom-[-60px] w-40 h-40 rounded-full bg-[#5C8A72]/10" aria-hidden />
      <div className="relative max-w-xl">
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#D98E73] font-semibold"><Radio size={14} /> Live & upcoming</span>
        <h2 className="font-serif text-3xl md:text-4xl text-[#1A242B] mt-3" data-testid="media-live-placeholder-title">The studio is quiet for now.</h2>
        <p className="text-[#4A5D6B] mt-3 leading-relaxed">
          Our enhancers are preparing their next live broadcasts and classes. Check back soon, or let us tell you the moment a session starts.
        </p>
        <div className="mt-6">
          {user ? <NotifyButton /> : (
            <div className="space-y-3">
              <p className="text-sm text-[#4A5D6B]"><Link to="/login?next=%2Fmedia%3Ftab%3Dlive" className="text-[#144F75] font-semibold underline" data-testid="media-live-signin">Sign in</Link> to get live alerts, or join our digest for upcoming sessions:</p>
              <NewsletterForm />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
